/////////////////////////////////////////////////////////////////////
// Copyright (c) Autodesk, Inc. All rights reserved
// Written by Autodesk Inventor Automation team
//
// Permission to use, copy, modify, and distribute this software in
// object code form for any purpose and without fee is hereby granted,
// provided that the above copyright notice appears in all copies and
// that both that copyright notice and the limited warranty and
// restricted rights notice below appear in all supporting
// documentation.
//
// AUTODESK PROVIDES THIS PROGRAM "AS IS" AND WITH ALL FAULTS.
// AUTODESK SPECIFICALLY DISCLAIMS ANY IMPLIED WARRANTY OF
// MERCHANTABILITY OR FITNESS FOR A PARTICULAR USE.  AUTODESK, INC.
// DOES NOT WARRANT THAT THE OPERATION OF THE PROGRAM WILL BE
// UNINTERRUPTED OR ERROR FREE.
/////////////////////////////////////////////////////////////////////
import axios from 'axios';

const AuthorizationHeader = 'Authorization';

/** Kind-of data layer, which hides all interaction with backend. */
class Repository {

    getAuthHeaders() {
        const token = localStorage.getItem('token');
        if (token) {
            return { 'Authorization': 'Bearer ' + token };
        }
        return {};
    }

    /** Get list of projects */
    async loadProjects() {
        const response = await axios.get("/projects", {
            headers: this.getAuthHeaders()
        });
        return response.data;
    }

    /** Delete projects */
    async deleteProjects(projectList) {
        const response = await axios.delete("/projects", {
            headers: {
                'content-type': 'application/json',
                ...this.getAuthHeaders()
            },
            data: JSON.stringify(projectList)
        });
        return response.data;
    }

    /** Get project parameters */
    async loadParameters(projectName) {
        const response = await axios.get("/parameters/" + projectName, {
            headers: this.getAuthHeaders()
        });
        return response.data;
    }

    /** Get the information if we should show the strip informing customer that parameters changed */
    async loadShowParametersChanged() {
        const response = await axios.get("/showParametersChanged", {
            headers: this.getAuthHeaders()
        });
        return response.data;
    }

    /** Send to server if we want to permanently enable/disable the showing of parameters changed banner */
    async sendShowParametersChanged(value) {
        const response = await axios.post("/showParametersChanged", value, {
            headers: {
                'content-type': 'application/json',
                ...this.getAuthHeaders()
            }
        });
        return response.data;
    }

    /** Load user's profile */
    async loadProfile() {
        const response = await axios.get("/api/autodesk/login/profile", {
            headers: this.getAuthHeaders()
        });
        return response.data;
    }

    /**Uploads package to the server */
    async uploadPackage(form) {
        const formData = new FormData();
        formData.append('package', form.file);
        formData.append('root', form.root);

        const result = await axios.post('/projects', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
                ...this.getAuthHeaders()
            }
        });
        return result.data;
    }

    getAccessCode() { return this._accessCode; }

    setAccessCode(value) {
        this._accessCode = value['code'];
        axios.defaults.headers.common[AuthorizationHeader] = `Code ${value['code']}, State ${value['state']}`;
    }

    forgetAccessCode() {
        delete axios.defaults.headers.common[AuthorizationHeader];
        delete this._accessCode;
    }

    hasAccessCode() { return !! this._accessCode; }

    /** Get BOM data */
    async loadBom(bomUrl) {
        const response = await axios.get(bomUrl, {
            headers: this.getAuthHeaders()
        });
        return response.data;
    }

    /** Get list of drawings */
    async loadDrawingsList(drawingsListUrl) {
        const response = await axios.get(drawingsListUrl, {
            headers: this.getAuthHeaders()
        });
        return response.data;
    }
}

/** Singleton with repo */
export default new Repository();